import { useCallback, useMemo, useState } from 'react'

/**
 * Seleção de linhas guardada por id, não por índice.
 *
 * O MUIDataTable trabalha com índice de linha: `rowsSelected: [3]` quer dizer
 * "a quarta linha da página atual". Isso é estável só enquanto os dados não
 * mudam. Trocou o filtro, a página ou a ordenação, o índice 3 passa a ser
 * outro host — e a ação em massa cai no equipamento errado, sem nada na tela
 * indicando isso.
 *
 * Guardando id, o índice deixa de ser estado e vira coisa derivada: a cada
 * página carregada, calcula-se quais linhas dela estão marcadas. A seleção
 * sobrevive a recarga, à troca de página e ao filtro, e o que a ação recebe é
 * sempre id.
 *
 * Junto do id guardamos nome e IP. Não é cache por desempenho: sem isso, um
 * host selecionado na página 1 vira um número solto quando a pessoa está na
 * página 3, e não haveria como listar a seleção sem ir ao servidor buscar algo
 * que estava na tela no momento do clique.
 *
 * As contas puras ficam exportadas porque é nelas que mora o defeito possível,
 * e assim dá pra testá-las sem montar a tabela.
 */

/** O que guardamos de cada host selecionado. */
export function resumoDaLinha(linha) {
    if (!linha || !linha.id) return null
    return { id: linha.id, nome: linha.host_name, ip: linha.ip }
}

/** Índices que o MUIDataTable deve desenhar marcados nesta página. */
export function indicesMarcados(tableData = [], itens = []) {
    const marcados = new Set(itens.map((i) => i.id))
    return tableData.reduce((acc, linha, i) => {
        if (linha && marcados.has(linha.id)) acc.push(i)
        return acc
    }, [])
}

/**
 * Nova seleção depois de um clique na tabela.
 *
 * O clique só fala sobre a página atual: quem está nela e não veio marcado
 * saiu, quem veio marcado entrou. Host selecionado em OUTRA página não é
 * mencionado pelo evento e precisa ser preservado — tratar esse silêncio como
 * "desmarcado" apagaria a seleção a cada paginação.
 */
export function proximaSelecao(anterior = [], tableData = [], rowsSelecionadas = []) {
    const idsDaPagina = new Set(tableData.map((l) => l && l.id).filter(Boolean))
    const marcadosAgora = (rowsSelecionadas || [])
        .map((i) => resumoDaLinha(tableData[i]))
        .filter(Boolean)

    const proximo = anterior.filter((item) => !idsDaPagina.has(item.id))
    marcadosAgora.forEach((item) => {
        if (!proximo.some((j) => j.id === item.id)) proximo.push(item)
    })
    return proximo
}

export default function useSelecaoPorId(tableData = []) {
    const [itens, setItens] = useState(() => [])

    const ids = useMemo(() => itens.map((i) => i.id), [itens])

    const rowsSelected = useMemo(
        () => indicesMarcados(tableData, itens), [tableData, itens])

    const onRowSelectionChange = useCallback((_cur, _all, rowsSelecionadas) => {
        setItens((anterior) => proximaSelecao(anterior, tableData, rowsSelecionadas))
    }, [tableData])

    const limpar = useCallback(() => setItens([]), [])

    const remover = useCallback((id) => {
        setItens((anterior) => anterior.filter((i) => i.id !== id))
    }, [])

    // Quantos dos selecionados não estão visíveis agora.
    const foraDaPagina = useMemo(() => {
        const idsDaPagina = new Set(tableData.map((l) => l && l.id).filter(Boolean))
        return itens.filter((i) => !idsDaPagina.has(i.id)).length
    }, [tableData, itens])

    return { ids, itens, rowsSelected, onRowSelectionChange, limpar, remover, foraDaPagina }
}
